/**
 * battle-participant — build a battle-ready participant from a loose species
 * identifier.
 *
 * Callers across the overworld pass a Surggie's identity in several forms: a
 * numeric SPECIES_DEX id ("3"), an overworld sprite key ("char_surggie_3" or
 * "surggie_3"), or a species name ("Byte"). This resolves any of those to the
 * real SpeciesData and derives the stats / types / moveset the BattleEngine and
 * battle UI expect — so combatants are no longer built with empty moves and
 * placeholder stats.
 */

import { SPECIES_DEX, type SpeciesData } from "@/data/species";
import { MOVES_DATA } from "@/data/moves";
import { getMovesLearnedAtLevel } from "@/engine/move-learn";

export interface BattleParticipantInit {
  /** A sprite-resolvable id (the species' `sprite` field, e.g. "surggie_3"). */
  speciesId: string;
  level: number;
  hpCurrent: number;
  hpMax: number;
  moves: string[];
  pp: Record<string, number>;
  stats: { atk: number; def: number; spd: number };
  types: string[];
  name: string;
}

// Tackle + Growl — universal fallback so the FIGHT menu is never empty.
const DEFAULT_MOVES = ["11", "12"] as const;

/** Resolve a loose identifier to SpeciesData, or undefined if no match. */
export function resolveSpecies(idOrKey: string): SpeciesData | undefined {
  if (!idOrKey) return undefined;
  const raw = idOrKey.trim();

  // Numeric SPECIES_DEX id, e.g. "3".
  const numeric = Number.parseInt(raw, 10);
  if (Number.isFinite(numeric) && String(numeric) === raw) {
    const byId = SPECIES_DEX.find((s) => s.id === numeric);
    if (byId) return byId;
  }

  // Sprite key form: "char_surggie_3" / "surggie_3".
  const spriteKey = raw.replace(/^char_/, "");
  const bySprite = SPECIES_DEX.find((s) => s.sprite === spriteKey);
  if (bySprite) return bySprite;

  // Species name, e.g. "Byte".
  const byName = SPECIES_DEX.find(
    (s) => s.name.toLowerCase() === raw.toLowerCase(),
  );
  if (byName) return byName;

  return undefined;
}

/** Cumulative moveset a species knows by `level` (last 4 learned). */
export function movesForSpecies(species: SpeciesData, level: number): string[] {
  const learned: string[] = [];
  for (let lvl = 1; lvl <= level; lvl++) {
    for (const id of getMovesLearnedAtLevel(species.name, lvl)) {
      if (!learned.includes(id)) learned.push(id);
    }
  }
  return learned.length ? learned.slice(-4) : [...DEFAULT_MOVES];
}

/**
 * Build a fully-populated battle participant. `idOrKey` may be a numeric
 * species id, a sprite key, or a species name; unresolved ids fall back to the
 * first species so a battle can always start.
 */
export function buildParticipant(
  idOrKey: string,
  level: number,
  hpCurrent: number,
  hpMax: number,
  name?: string,
): BattleParticipantInit {
  const species = resolveSpecies(idOrKey) ?? SPECIES_DEX[0];
  const moves = movesForSpecies(species, level);

  const pp: Record<string, number> = {};
  for (const moveId of moves) {
    const move = MOVES_DATA.find((m) => m.id === Number.parseInt(moveId, 10));
    pp[moveId] = move?.pp ?? 20;
  }

  return {
    speciesId: species.sprite,
    level,
    hpCurrent,
    hpMax,
    moves,
    pp,
    stats: { atk: species.baseAtk, def: species.baseDef, spd: species.baseSpe },
    types: [species.type1, species.type2].filter(
      (t): t is string => Boolean(t),
    ),
    name: name ?? species.name,
  };
}
